import { SB } from '@play-co/replicant';
import {
  createFlexMessageTemplate,
  createFlexContainer,
  createOpenGameAction,
} from './utils';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import { liveOpsEventType } from '../chatbot.ruleset';

/**
 * Assets for live ops messages
 */
export const liveOpsAssets: Record<string, string> = {
  'liveops/fuji/ja': 'private/chatbot/liveops/fujiquest_ja.png',
  'liveops/fuji/en': 'private/chatbot/liveops/fujiquest_en.png',

  // Scheduled events
  'liveops/lampsStart/ja': 'private/chatbot/liveops/magiclampstart_ja.png',
  'liveops/lampsStart/en': 'private/chatbot/liveops/magiclampstart_en.png',
  'liveops/lampsEnd/ja': 'private/chatbot/liveops/magiclampend_ja.png',
  'liveops/lampsEnd/en': 'private/chatbot/liveops/magiclampend_en.png',
  'liveops/matchLife/ja': 'private/chatbot/liveops/matchlife_ja.png',
  'liveops/matchLife/en': 'private/chatbot/liveops/matchlife_en.png',
  'liveops/waterballoon/ja': 'private/chatbot/liveops/waterballoon_ja.png',
  'liveops/waterballoon/en': 'private/chatbot/liveops/waterballoon_en.png',
  'liveops/moveLikeBuruburu/ja': 'private/chatbot/liveops/moves_ja.png',
  'liveops/moveLikeBuruburu/en': 'private/chatbot/liveops/moves_en.png',
  'liveops/lollipopWednesday/ja': 'private/chatbot/liveops/lollipop_ja.png',
  'liveops/lollipopWednesday/en': 'private/chatbot/liveops/lollipop_en.png',
  'liveops/streakChampion/ja': 'private/chatbot/liveops/streaks_ja.png',
  'liveops/streakChampion/en': 'private/chatbot/liveops/streaks_en.png',
  'liveops/yellowFestival/ja': 'private/chatbot/liveops/yellow_ja.png',
  'liveops/yellowFestival/en': 'private/chatbot/liveops/yellow_en.png',
  'liveops/miniFirecracker/ja': 'private/chatbot/liveops/crackers_ja.png',
  'liveops/miniFirecracker/en': 'private/chatbot/liveops/crackers_en.png',
  'liveops/turtleRace/ja': 'private/chatbot/liveops/turtlerace_ja.png',
  'liveops/turtleRace/en': 'private/chatbot/liveops/turtlerace_en.png',

  'liveops/tournamentStart/streak/ja':
    'private/chatbot/liveops/flyinghigh_start_ja.png',
  'liveops/tournamentStart/streak/en':
    'private/chatbot/liveops/flyinghigh_start_en.png',
  'liveops/tournamentTomorrow/streak/ja':
    'private/chatbot/liveops/flyinghigh_endtomorrow_ja.png',
  'liveops/tournamentTomorrow/streak/en':
    'private/chatbot/liveops/flyinghigh_endtomorrow_en.png',
  'liveops/tournamentSoon/streak/ja':
    'private/chatbot/liveops/flyinghigh_endsoon_ja.png',
  'liveops/tournamentSoon/streak/en':
    'private/chatbot/liveops/flyinghigh_endsoon_en.png',
  'liveops/tournamentEnd/streak/ja':
    'private/chatbot/liveops/flyinghigh_ended_ja.png',
  'liveops/tournamentEnd/streak/en':
    'private/chatbot/liveops/flyinghigh_ended_en.png',

  'liveops/tournamentStart/star/ja':
    'private/chatbot/liveops/startournament_start_ja.png',
  'liveops/tournamentStart/star/en':
    'private/chatbot/liveops/startournament_start_en.png',
  'liveops/tournamentTomorrow/star/ja':
    'private/chatbot/liveops/startournament_endtomorrow_ja.png',
  'liveops/tournamentTomorrow/star/en':
    'private/chatbot/liveops/startournament_endtomorrow_en.png',
  'liveops/tournamentSoon/star/ja':
    'private/chatbot/liveops/startournament_endsoon_ja.png',
  'liveops/tournamentSoon/star/en':
    'private/chatbot/liveops/startournament_endsoon_en.png',
  'liveops/tournamentEnd/star/ja':
    'private/chatbot/liveops/startournament_ended_ja.png',
  'liveops/tournamentEnd/star/en':
    'private/chatbot/liveops/startournament_ended_en.png',
};

/**
 * Create live ops messages
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const liveOpsMessage = createFlexMessageTemplate(
  SB.object({
    type: SB.tuple(liveOpsEventType),
    subType: SB.tuple(['streak', 'star']).optional(),
    locale: SB.tuple(['en', 'ja']),
  }),
  (args, payload, api) => {
    const type = args.type + (args.subType ? `/${args.subType}` : '');
    const altText = getLocaleConfig(args.locale).liveOps[args.type].text;
    const template = createFlexContainer(altText, {
      type: 'bubble',
      size: 'giga',
      action: createOpenGameAction(altText, payload),
      body: {
        type: 'box',
        layout: 'horizontal',
        contents: [
          {
            type: 'image',
            url: api.getAssetPath(`liveops/${type}/${args.locale}`),
            size: 'full',
            aspectMode: 'cover',
            aspectRatio: '1.51:1',
            gravity: 'center',
          },
        ],
        paddingAll: '0px',
        justifyContent: 'center',
      },
    });

    return template;
  },
);
